/**
 * Trims text to be under 300 characters while preserving links and sentence boundaries
 * @param {string} text - The input text to trim
 * @returns {string} - The trimmed text
 */
export function trimText(text) {
  try {
    // Handle empty or invalid input
    if (!text || typeof text !== 'string') {
      return '';
    }

    // Extract URLs using regex
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex) || [];
    
    // Remove URLs from text for processing
    let mainText = text.replace(urlRegex, '').trim();
    
    // Split text into sentences while preserving punctuation
    const sentences = [];
    let currentSentence = '';
    let i = 0;
    
    while (i < mainText.length) {
      // Check for sentence endings (。)
      if (mainText[i] === '。') {
        currentSentence += mainText[i];
        if (currentSentence.trim()) {
          sentences.push(currentSentence.trim());
        }
        currentSentence = '';
      } else {
        currentSentence += mainText[i];
      }
      i++;
    }
    
    // Add any remaining text
    if (currentSentence.trim()) {
      sentences.push(currentSentence.trim());
    }
    
    // Remove duplicates and join with proper spacing
    const uniqueSentences = [...new Set(sentences)];
    mainText = uniqueSentences.map((sentence, index) => {
      // For first sentence
      if (index === 0) {
        return sentence + '\n\n';
      }
      // Keep original sentence as is
      return (index === uniqueSentences.length - 1) ? sentence : sentence + ' ';
    }).join('');
    
    // Calculate URL portion and keep trimming until total length is under 300
    const urlPortion = urls.length ? '\n\n' + urls.join('\n\n') : '';
    // If text is already under limit, return as is
    if ((mainText + urlPortion).length <= 300) {
      return mainText.trim() + urlPortion;
    }

    // Find the last complete sentence that fits within the limit
    let currentText = mainText;
    let lastValidBreak = -1;
    
    // Start from the end and find the first '。' that keeps text under limit
    for (let i = currentText.length - 1; i >= 0; i--) {
      if (currentText[i] === '。') {
        if ((currentText.substring(0, i + 1) + urlPortion).length <= 300) {
          lastValidBreak = i + 1; // Include the '。'
          break;
        }
      }
    }

    // If we found a valid break point, use it
    if (lastValidBreak > 0) {
      mainText = currentText.substring(0, lastValidBreak);
    } else {
      // If no valid break point found, take as much as possible
      mainText = currentText.substring(0, Math.max(0, 300 - urlPortion.length));
      // Try to find the nearest '。' before this point
      const lastPeriod = mainText.lastIndexOf('。');
      if (lastPeriod > 0) {
        mainText = mainText.substring(0, lastPeriod + 1);
      }
    }
    
    // Reconstruct text with preserved URLs
    return mainText.trim() + (urls.length ? '\n\n' + urls.join('\n\n') : '');
  } catch (error) {
    console.error('Error in trimText:', error);
    return text; // Return original text if error occurs
  }
}
